import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { getYcoaWithoutClient } from "@/api/coa-validation";
import { NO_VALUE } from "@/components/projects/diagnostic/sectionState";
import { formatCount } from "@/lib/format";

/**
 * As contas do YCOA que nenhuma conta do cliente recebe (#1715, cartão 7.2 do Marco).
 *
 * Na renumeração da SAP, toda conta do YCOA recebe uma conta do cliente ou é
 * marcada Not Used. Nesta versão é só uma lista, sem decisão, com as marcas "usada
 * em configuração" e "modelo de N contas": o Not Used fica para o Explore. É
 * informação de curadoria, então só o consultor vê, e ela nasce fechada.
 */
export function YcoaWithoutClientSection({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [onlyConfiguration, setOnlyConfiguration] = useState(false);
  const { data } = useQuery({
    queryKey: ["coa-validation", projectId, "ycoa-without-client"],
    queryFn: () => getYcoaWithoutClient(projectId),
    enabled: !!projectId,
  });
  if (!data) return null;

  const used = data.accounts.filter((account) => account.used_in_configuration);
  const accounts = onlyConfiguration ? used : data.accounts;
  const Icon = open ? ChevronDown : ChevronRight;

  return (
    <div data-testid="ycoa-without-client" className="rounded-lg border border-[#e8ebf0] bg-white">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full flex-wrap items-center gap-2 px-4 py-3 text-left text-[13px] text-[#08254c] hover:bg-[#f7f8fa]"
      >
        <Icon aria-hidden className="h-4 w-4" />
        <span className="font-semibold">{t("YCOA accounts without a client account")}</span>
        <span className="text-[#596b85]">
          {t("{{missing}} of {{total}} YCOA accounts receive no client account", {
            missing: formatCount(data.accounts.length),
            total: formatCount(data.total_ycoa),
          })}
        </span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-[#e8ebf0] px-4 py-3 text-[12px]">
          <p className="max-w-3xl text-[#596b85]">
            {t(
              "In the SAP renumbering, every YCOA account receives a client account or is marked Not Used. Here it is only a list, with no decision: Not Used is decided in Explore.",
            )}
          </p>
          <label className="inline-flex items-center gap-2 text-[#08254c]">
            <input
              type="checkbox"
              checked={onlyConfiguration}
              onChange={(event) => setOnlyConfiguration(event.target.checked)}
            />
            {t("Only used in configuration ({{used}})", { used: formatCount(used.length) })}
          </label>
          <div className="max-h-[420px] overflow-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 border-b border-[#e8ebf0] bg-white font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium">{t("YCOA account")}</th>
                  <th className="px-3 py-2 font-medium">{t("Description")}</th>
                  <th className="px-3 py-2 font-medium">{t("Marks")}</th>
                  <th className="px-3 py-2 font-medium">{t("Where used")}</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <tr
                    key={account.account}
                    data-testid={`ycoa-without-client-${account.account}`}
                    className="border-b border-[#f2f4f7] align-top text-[#08254c]"
                  >
                    <td className="px-3 py-2 font-mono tabular-nums">{account.account}</td>
                    <td className="px-3 py-2">{account.description ?? NO_VALUE}</td>
                    <td className="px-3 py-2">
                      {[
                        account.used_in_configuration ? t("Used in configuration") : null,
                        account.model_of > 0
                          ? t("Model of {{count}} accounts", { count: account.model_of })
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ") || NO_VALUE}
                    </td>
                    <td className="px-3 py-2 text-[#596b85]">{account.where_used ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
